// ═══════════════════════════════════════════════════════════════════
//  FLOW WEAVER — CORE ENTITY TYPES
// ═══════════════════════════════════════════════════════════════════
//
//  Three distinct entities exist in a workflow:
//
//  ┌─────────┐   ┌─────────┐   ┌─────────┐
//  │  BLOCK  │   │  GROUP  │   │  LAYER  │
//  │ (node)  │   │ (frame) │   │ (card)  │
//  └─────────┘   └─────────┘   └─────────┘
//
//  BLOCK  — A single processing step or decision point.
//           Rendered as a card with 8 connection ports.
//           Cannot contain other entities.
//
//  GROUP  — A visual frame around blocks (collapsed=false).
//           Blocks remain visible and individually editable.
//           Used for organization — does NOT create navigation.
//
//  LAYER  — A navigable container (collapsed=true).
//           Rendered as a compact card on the parent canvas.
//           Double-click to enter and see its contents.
//           Creates a new navigation depth level.
//           Can be empty (placeholder for future content).
//
//  The ONLY difference between a Group and a Layer is the
//  `collapsed` boolean on BlockGroup. Everything else is shared.
//
// ═══════════════════════════════════════════════════════════════════

/** Available block types. Use 'general' for any step not covered. Use 'condition' for decisions. */
export type BlockType =
  | 'create-user'
  | 'disable-mailbox'
  | 'delete-m365'
  | 'send-email'
  | 'webhook'
  | 'add-to-group'
  | 'condition'
  | 'sheet-link'
  | 'general';

/** Configuration attached to a block — name is required, rest is optional. */
export interface BlockConfig {
  name: string;
  /** Kort sammanfattning som syns på block-kortet i canvasen. Plain text. */
  description?: string;
  /** Lång markdown-beskrivning som visas i Detaljer-dialogen tillsammans
   *  med bilagor. Hålls åtskild från `description` så user kan ha en
   *  enkel summering på kortet och en utförlig text i detail-vyn. När
   *  fältet saknas faller dialogen tillbaka till `description`. */
  detailedDescription?: string;
  icon?: string;
  to?: string;
  subject?: string;
  /**
   * Atlas: cross-document navigation target. When set on a block (typically
   * with type='sheet-link'), double-clicking the block navigates to the
   * referenced sheet/document.
   *
   * Stored as a flat record so the existing index signature remains valid.
   * Use `getLinkTarget(config)` / `setLinkTarget(config, target)` helpers
   * for type-safe access. See `src/lib/sheetLink.ts`.
   *
   * Format: `"{documentId}|{sheetId}|{blockId}"` (last two optional).
   */
  linkTarget?: string;
  [key: string]: string | undefined;
}

/**
 * BLOCK — A single node in the workflow.
 *
 * Rendered as a ~200px wide card with 8 connection ports.
 * Position (x, y) is the CENTER of the block (not top-left).
 * All coordinates must be multiples of 20 (grid snap).
 */
export interface WorkflowBlock {
  id: string;
  type: BlockType;
  /** Center X coordinate. Must be a multiple of 20. */
  x: number;
  /** Center Y coordinate. Must be a multiple of 20. */
  y: number;
  config: BlockConfig;
  color: string;
  /**
   * Client-local "modified at" timestamp (epoch ms). Bumped on every
   * mutation so realtime peers can skip older-than-local updates and
   * avoid clobbering in-progress edits. Optional — absent means "not
   * mtime-tracked yet" (treat as 0). See `src/lib/workflowDelta.ts`.
   */
  mtime?: number;
  /**
   * P-007: Optional port-schemas för typing av in/out-data. Används
   * av runner (P-002) för runtime-validering: om `in` är satt och
   * input-struktur inte matchar → step rejectas tidigt. `out` valideras
   * efter execute → mismatch = warning (non-fatal, loggas).
   * JSON-Schema-like shape; full validator-bib:et importeras lazy
   * i runner-kontext för att hålla bundle-size nere.
   */
  portSchemas?: PortSchemas;
}

/** P-007: Minimal JSON-Schema-subset för port-validering. */
export interface JsonSchemaLike {
  type?: 'string' | 'number' | 'boolean' | 'object' | 'array' | 'null';
  /** För objects: properties per key. */
  properties?: Record<string, JsonSchemaLike>;
  /** För objects: required keys. */
  required?: readonly string[];
  /** För arrays: item-schema. */
  items?: JsonSchemaLike;
  /** Optional human-readable beskrivning för UI. */
  description?: string;
}

export interface PortSchemas {
  /** Förväntad shape på inkommande input. Runner rejectar om missmatch. */
  in?: JsonSchemaLike;
  /** Förväntad shape på utgående output. Warning vid missmatch. */
  out?: JsonSchemaLike;
}

/** Alias for WorkflowBlock — kept for legacy imports across the codebase. */
export type Block = WorkflowBlock;

/** 8-directional port system for connector routing. */
export type PortSide =
  | 'top'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left'
  | 'left'
  | 'top-left';

/**
 * CONNECTOR — An edge between two entities.
 *
 * Endpoints (fromId/toId) can reference a Block ID or a Group ID.
 * When connecting to a collapsed group (layer), the connector
 * attaches to the layer card.
 */
export interface Connector {
  id: string;
  /** Source entity — block ID or group ID */
  fromId: string;
  /** Target entity — block ID or group ID */
  toId: string;
  fromPort?: PortSide;
  toPort?: PortSide;
  waypoints?: { x: number; y: number }[];
  label?: string;
  /**
   * Optional CSS color (hex like `#3b82f6` or `hsl(...)`) for the line +
   * arrowhead. When absent, ConnectorPath falls back to the theme primary.
   * Persisted on the connector so color survives reload.
   */
  color?: string;
  /**
   * When true, ConnectorPath renders an arrowhead at BOTH ends of the
   * line (mirrored at the source). Default false → single arrow at target.
   */
  bidirectional?: boolean;
  /**
   * When true, the line gets a flowing dash animation that travels from
   * source → target, suggesting "data is moving". Arrowhead does NOT
   * animate. Animation respects `prefers-reduced-motion: reduce`.
   */
  pulse?: boolean;
  /**
   * Optional override color for the pulsing line only — does not affect
   * arrowhead or static color. Falls back to `connector.color` when absent.
   */
  pulseColor?: string;
  /** See WorkflowBlock.mtime. */
  mtime?: number;
}

/**
 * GROUP / LAYER — A container for blocks.
 *
 * The `collapsed` field determines how this container behaves:
 *
 *   collapsed=false (or undefined) → GROUP
 *     - Rendered as a dashed frame around its member blocks
 *     - Visual grouping only — blocks remain visible
 *     - Does NOT create a navigation level
 *
 *   collapsed=true → LAYER
 *     - Rendered as a compact card (200×70px) on the parent canvas
 *     - Double-click to navigate inside
 *     - Creates a new depth level in the navigation hierarchy
 *     - Requires collapsedX/collapsedY for card position
 *     - Can start empty (blockIds=[]) as a placeholder
 *
 * Nesting: Use parentGroupId to place groups inside other groups.
 * There is no depth limit — layers can nest infinitely.
 */
export interface BlockGroup {
  id: string;
  /** IDs of blocks directly owned by this group. Each block belongs to ONE group only. */
  blockIds: string[];
  color: string;
  /** When true, dragging any member block moves the entire group as a unit. */
  locked?: boolean;
  /** Display name shown in the group header or layer card. */
  name?: string;
  /** Parent group ID for nesting. null/undefined = top-level. */
  parentGroupId?: string;
  /**
   * The key distinction:
   * - false/undefined = GROUP (visual frame, blocks visible)
   * - true = LAYER (navigable card, double-click to enter)
   */
  collapsed?: boolean;
  /** X position of the layer card on parent canvas. Required when collapsed=true. */
  collapsedX?: number;
  /** Y position of the layer card on parent canvas. Required when collapsed=true. */
  collapsedY?: number;
  /** Bounding box of member blocks when rendered as a group frame. Auto-computed. */
  boundsLeft?: number;
  boundsTop?: number;
  boundsRight?: number;
  boundsBottom?: number;
  /** See WorkflowBlock.mtime. */
  mtime?: number;
  /**
   * Marker för layers som skapats av onboarding-touren. Sådana layers är
   * sandboxar — de rensas automatiskt när touren avslutas och räknas inte
   * in i user's "riktiga" workflow. Committing av en tour via
   * `commitTourStage` flippar flaggan till false och gör layer till en
   * vanlig layer.
   */
  isTourSandbox?: boolean;
  /**
   * Marker för layers som är AI-proposals (pending approval). Samma
   * cleanup-mönster som isTourSandbox — `cleanupStaleAiLayers` tar bort
   * övergivna proposals vid mount. `approveAiStage` flippar tillbaka
   * flaggan till false.
   */
  isAiProposal?: boolean;
  /** Correlation-id på open credit-reservation som kopplar till proposal:et. */
  aiCorrelationId?: string;
  /** Uppskattad kostnad i credits — visas i layer-header som badge. */
  aiCostCredits?: number;
  /**
   * ATLAS: When set, this layer is a virtual container that points to
   * another document in the atlas. Double-clicking the layer card loads
   * the referenced document instead of entering this group's blockIds[].
   *
   * Atlas layers always have collapsed=true and blockIds=[] (empty).
   * Their content is loaded on-demand when the user enters them.
   *
   * See `src/lib/atlasView.ts` for the builder, and
   * `documents/v1/ATLAS_VIEW.md` for the design.
   */
  atlasRef?: AtlasLayerRef;
}

/**
 * Atlas reference attached to a Layer (collapsed BlockGroup).
 * Tells FW that this Layer is a virtual container pointing to another
 * document, not a regular sub-canvas.
 */
export interface AtlasLayerRef {
  /** Target AtlasDocument.id */
  documentId: string;
  /** Optional: which sheet to land on (default: first). */
  initialSheetId?: string;
  /** Optional: scroll/highlight this block after navigation. */
  targetBlockId?: string;
}

/** Complete workflow state — the root document. */
export interface WorkflowState {
  name: string;
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  coordinateOrigin?: 'center';
}

export interface BlockDimensions {
  width: number;
  height: number;
}

export interface ToolItem {
  type: BlockType;
  icon: string;
  label: string;
}

// ═══════════════════════════════════════════════════════════════════
//  TYPE HELPERS — Use these to distinguish entity roles
// ═══════════════════════════════════════════════════════════════════

/** Returns true if this group is a LAYER (collapsed, navigable). */
export function isLayer(group: BlockGroup): boolean {
  return group.collapsed === true;
}

/** Returns true if this group is a visible GROUP frame (not collapsed). */
export function isGroupFrame(group: BlockGroup): boolean {
  return !group.collapsed;
}

/** Returns true if this group has no parent (sits at root level). */
export function isTopLevel(group: BlockGroup): boolean {
  return !group.parentGroupId;
}

/** Returns the nesting depth of a group (0 = top-level). */
export function getGroupDepth(group: BlockGroup, allGroups: BlockGroup[]): number {
  let depth = 0;
  let current = group.parentGroupId;
  while (current) {
    depth++;
    const parent = allGroups.find(g => g.id === current);
    current = parent?.parentGroupId;
  }
  return depth;
}

// ═══════════════════════════════════════════════════════════════════
//  MULTI-SHEET SYSTEM
// ═══════════════════════════════════════════════════════════════════
//
//  A workflow can contain multiple sheets (tabs), each with its own
//  canvas (blocks, connectors, groups). Sheets are conceptually
//  stacked vertically and blocks can be linked between adjacent
//  sheets only (N ↔ N±1).
//
//  Single-sheet workflows use the legacy WorkflowState format.
//  Multi-sheet workflows use MultiSheetWorkflowState.
//  The system auto-wraps legacy format on load.
//
// ═══════════════════════════════════════════════════════════════════

/** Optional metadata attached to a sheet. */
export interface SheetMeta {
  id?: string;
  description?: string;
  color?: string;
  icon?: string;
}

/** A single sheet/tab — an independent canvas with its own entities. */
export interface WorkflowSheet {
  id: string;
  name: string;
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  meta?: SheetMeta;
}

/**
 * Cross-sheet connector linking blocks on different sheets.
 *
 * Constraint (Ship 7 / Caveat 5A): fromSheetIndex !== toSheetIndex.
 * The original adjacency rule (|i-j|===1) was lifted so atlas imports
 * with non-adjacent cross-doc refs can preserve their connector graph
 * instead of demoting them to sheet-link blocks. Renderers must
 * tolerate long-distance links (typically rendered as a tab-edge
 * indicator with sheet number, not a continuous line on canvas).
 */
export interface CrossSheetConnector {
  id: string;
  fromSheetIndex: number;
  toSheetIndex: number;
  fromBlockId: string;
  toBlockId: string;
  fromPort?: PortSide;
  toPort?: PortSide;
  label?: string;
}

/** Multi-sheet workflow state — the root document format for tabbed workflows. */
export interface MultiSheetWorkflowState {
  name: string;
  sheets: WorkflowSheet[];
  crossSheetConnectors: CrossSheetConnector[];
  activeSheetIndex: number;
  coordinateOrigin?: 'center';
}

/** Type guard: is this state multi-sheet? */
export function isMultiSheetState(state: unknown): state is MultiSheetWorkflowState {
  return state != null && typeof state === 'object'
    && 'sheets' in state && Array.isArray((state as Record<string, unknown>).sheets);
}

/** Convert legacy single-sheet state to multi-sheet format. */
export function wrapSingleSheet(state: WorkflowState): MultiSheetWorkflowState {
  return {
    name: state.name,
    sheets: [{
      id: crypto.randomUUID(),
      // Tom user-text — sifferprefix (① för index 0) deriveras från
      // position vid render via getSheetDisplayName(). Se sheetNaming.ts.
      name: '',
      blocks: state.blocks,
      connectors: state.connectors,
      groups: state.groups,
    }],
    crossSheetConnectors: [],
    activeSheetIndex: 0,
    coordinateOrigin: state.coordinateOrigin,
  };
}

/** Extract a single sheet as a legacy WorkflowState (for components that expect it). */
export function extractSheetState(
  multi: MultiSheetWorkflowState,
  sheetIndex: number,
): WorkflowState {
  const sheet = multi.sheets[sheetIndex];
  return {
    name: multi.name,
    blocks: sheet?.blocks ?? [],
    connectors: sheet?.connectors ?? [],
    groups: sheet?.groups ?? [],
    coordinateOrigin: multi.coordinateOrigin,
  };
}
